import React, { useState, useEffect, useRef } from "react";
import HandIc from "../../assets/svg/hand.svg";
import "./paymenthistoryList.css";
import { Dropdown, Toast, ToastContainer, Spinner } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { ReactComponent as RarIc } from "../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../assets/svg/lar_ic.svg";
import { ReactComponent as DownArrow } from "../../assets/svg/down_arror.svg";
import {
  getPaymentReconciliationListAPI,
  getUnlinkedBankTransactionsAPI,
  linkTransactionAPI,
  validateReconciliationsAPI,
} from "./payment-api";

const LinkTransactionModal = ({ isOpen, onClose, onSubmit, submitting }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [bankTxns, setBankTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null);
    const fetchUnlinked = async () => {
      setLoading(true);
      try {
        const response = await getUnlinkedBankTransactionsAPI({ search });
        if (response.success) {
          setBankTxns(response.transactions || []);
        } else {
          setBankTxns([]);
        }
      } catch (error) {
        console.error("Error fetching unlinked bank transactions:", error);
        setBankTxns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUnlinked();
  }, [isOpen, search]);

  if (!isOpen) return null;

  const formatAmount = (amt) => {
    if (amt === undefined || amt === null) return "--:--";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amt);
  };

  return (
    <div className="recon-modal-overlay">
      <div className="recon-modal-content link-transaction-modal">
        <div className="recon-modal-header align-items-center">
          <h4 className="mismatch-title">LINK TRANSACTION</h4>

          <div className="d-flex align-items-center gap-3 flex-grow-1 justify-content-end px-3">
            <div className="search-input-wrapper">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                  stroke="#94A3B8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search..."
                className="modal-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button
              className="btn-modal-submit d-flex align-items-center justify-content-center"
              disabled={selectedId === null || submitting}
              onClick={() => onSubmit(selectedId)}
            >
              {submitting ? (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ color: "#FFFFFF" }}
                />
              ) : (
                "Submit"
              )}
            </button>
          </div>

          <div className="modal-close-btn" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="#64748B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <div className="recon-modal-body">
          <div className="link-table-container">
            <table className="link-table">
              <thead>
                <tr>
                  <th style={{ width: "50px" }}></th>
                  <th style={{ width: "150px" }}>Txn Date</th>
                  <th style={{ width: "120px" }}>Value Date</th>
                  <th style={{ width: "150px" }}>Bank</th>
                  <th>Description</th>
                  <th style={{ width: "110px" }}>Cheque No</th>
                  <th style={{ width: "100px" }} className="text-end">
                    Amount
                  </th>
                  <th style={{ width: "110px" }} className="text-end">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : bankTxns.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      No unlinked bank transactions found
                    </td>
                  </tr>
                ) : (
                  bankTxns.map((row) => (
                    <tr
                      key={row.id}
                      className={selectedId === row.id ? "selected-row" : ""}
                      onClick={() => setSelectedId(row.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        <div
                          className={`custom-radio ${selectedId === row.id ? "checked" : ""}`}
                        >
                          {selectedId === row.id && (
                            <div className="radio-inner" />
                          )}
                        </div>
                      </td>
                      <td>{row.transaction_date}</td>
                      <td>{row.value_date}</td>
                      <td>{row.bank_name}</td>
                      <td title={row.description}>{row.description}</td>
                      <td>{row.cheque_number || "-"}</td>
                      <td className="text-end">
                        {formatAmount(row.spent_amount)}
                      </td>
                      <td className="text-end">{formatAmount(row.balance)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const MismatchModal = ({
  isOpen,
  onClose,
  onLinkClick,
  pair,
  onValidate,
  validating,
}) => {
  if (!isOpen || !pair) return null;

  const { payment, bank_transaction } = pair;

  const dateMatch =
    payment?.transaction_date === bank_transaction?.transaction_date;
  const chequeMatch =
    String(payment?.chequeNo || "").trim() ===
    String(bank_transaction?.cheque_number || "").trim();
  const amountMatch =
    Number(payment?.amount || 0) ===
    Number(bank_transaction?.spent_amount || 0);
  const hasBankVendor = Boolean(bank_transaction?.vendor_name);
  const vendorMatch =
    hasBankVendor &&
    String(payment?.vendor_name || "")
      .trim()
      .toLowerCase() ===
      String(bank_transaction?.vendor_name || "")
        .trim()
        .toLowerCase();

  const formatAmount = (amt) => {
    if (amt === undefined || amt === null) return "--:--";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amt);
  };

  return (
    <div className="recon-modal-overlay">
      <div className="recon-modal-content mismatch-modal">
        <div className="recon-modal-header">
          <h4
            className={`mismatch-title ${pair.status === "MATCHED" || pair.status === "VALIDATED" ? "matched" : ""}`}
          >
            {pair.status || "MISMATCH"}
          </h4>
          <div className="d-flex align-items-center gap-3">
            <button className="btn-link-transaction" onClick={onLinkClick}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 10-5.656-5.656l-1.102 1.101"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Link Transaction
            </button>
            <div className="modal-close-btn" onClick={onClose}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://@www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="#64748B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="recon-modal-body">
          <div className="mismatch-table-container">
            <table className="mismatch-table">
              <thead>
                <tr>
                  <th>INFORMATION</th>
                  <th>PAYMENT HISTORY</th>
                  <th>BANK TRANSACTION</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="info-label">Transaction Date</td>
                  <td
                    className={
                      dateMatch
                        ? "text-matched-green bg-matched-cell"
                        : "text-mismatch-red bg-mismatch-cell"
                    }
                  >
                    {payment?.transaction_date || "--:--"}
                  </td>
                  <td
                    className={
                      dateMatch
                        ? "text-matched-green bg-matched-cell"
                        : "text-mismatch-red bg-mismatch-cell"
                    }
                  >
                    {bank_transaction?.transaction_date || "--:--"}
                  </td>
                </tr>
                <tr>
                  <td className="info-label">Vendor</td>
                  <td
                    className={
                      hasBankVendor
                        ? vendorMatch
                          ? "text-matched-green bg-matched-cell"
                          : "text-mismatch-red bg-mismatch-cell"
                        : "textcolor"
                    }
                  >
                    {payment?.vendor_name || "--:--"}
                  </td>
                  <td
                    className={
                      hasBankVendor
                        ? vendorMatch
                          ? "text-matched-green bg-matched-cell"
                          : "text-mismatch-red bg-mismatch-cell"
                        : "text-muted-value"
                    }
                  >
                    {bank_transaction?.vendor_name || "--:--"}
                  </td>
                </tr>
                <tr>
                  <td className="info-label">Cheque</td>
                  <td
                    className={
                      chequeMatch
                        ? "text-matched-green bg-matched-cell"
                        : "text-mismatch-red bg-mismatch-cell"
                    }
                  >
                    {payment?.chequeNo || "--:--"}
                  </td>
                  <td
                    className={
                      chequeMatch
                        ? "text-matched-green bg-matched-cell"
                        : "text-mismatch-red bg-mismatch-cell"
                    }
                  >
                    {bank_transaction?.cheque_number || "--:--"}
                  </td>
                </tr>
                <tr>
                  <td className="info-label">Amount</td>
                  <td
                    className={
                      amountMatch
                        ? "text-matched-green bg-matched-cell"
                        : "text-mismatch-red bg-mismatch-cell"
                    }
                  >
                    {formatAmount(payment?.amount)}
                  </td>
                  <td
                    className={
                      amountMatch
                        ? "text-matched-green bg-matched-cell"
                        : "text-mismatch-red bg-mismatch-cell"
                    }
                  >
                    {formatAmount(bank_transaction?.spent_amount)}
                  </td>
                </tr>
                <tr>
                  <td className="info-label">Value date</td>
                  <td className="text-muted-value">--:--</td>
                  <td>{bank_transaction?.value_date || "--:--"}</td>
                </tr>
                <tr className="row-description">
                  <td className="info-label">Description</td>
                  <td className="text-muted-value">--:--</td>
                  <td className="text-wrap" style={{ maxWidth: "300px" }}>
                    {bank_transaction?.description || "--:--"}
                  </td>
                </tr>
                <tr>
                  <td className="info-label">Balance Amount</td>
                  <td className="text-muted-value">--:--</td>
                  <td>{formatAmount(bank_transaction?.balance)}</td>
                </tr>
                <tr>
                  <td className="info-label">Bank</td>
                  <td className="text-muted-value">--:--</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="bank-logo-small bg-danger d-flex align-items-center justify-content-center"
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "4px",
                        }}
                      >
                        {bank_transaction?.bank_name
                          ? bank_transaction.bank_name.charAt(0)
                          : "B"}
                      </div>
                      <span>{bank_transaction?.bank_name || "--:--"}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {pair.status === "MATCHED" && payment?.id && (
          <div className="d-flex justify-content-end px-3 pb-3">
            <button
              onClick={() => onValidate(payment.id)}
              disabled={validating}
              style={{
                backgroundColor: "#48BB78",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "100px",
                padding: "8px 32px",
                fontSize: "14px",
                fontWeight: "600",
                height: "38px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: validating ? "not-allowed" : "pointer",
                opacity: validating ? 0.7 : 1,
              }}
            >
              {validating ? (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ color: "#FFFFFF" }}
                />
              ) : (
                "Validate"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const PaymentReconciliation = ({
  search,
  status,
  fromDate,
  toDate,
  timePeriod,
  vendorId,
  onCountsChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [reconciliationList, setReconciliationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePair, setActivePair] = useState(null);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);

  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(null);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const showToast = (status, msg) => {
    setSnackbarStatus(status);
    setSnackbarMessage(msg);
    setShowSnackbar(true);
  };

  // Scroll synchronization
  const bankScrollRef = useRef(null);
  const trackRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleBankScroll = () => {
    if (bankScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = bankScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      setScrollProgress(progress);

      if (paymentScrollRef.current) {
        const payMaxScroll =
          paymentScrollRef.current.scrollWidth -
          paymentScrollRef.current.clientWidth;
        const targetScroll = progress * payMaxScroll;
        if (Math.abs(paymentScrollRef.current.scrollLeft - targetScroll) > 2) {
          paymentScrollRef.current.scrollLeft = targetScroll;
        }
      }
    }
  };

  const handleThumbDrag = (e) => {
    if (!trackRef.current || !bankScrollRef.current) return;
    e.preventDefault();

    const trackWidth = trackRef.current.getBoundingClientRect().width;
    const thumbWidth = 30;
    const maxThumbLeft = trackWidth - thumbWidth;

    const startX = e.clientX;
    const startProgress = scrollProgress;

    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      let newProgress = startProgress + deltaX / maxThumbLeft;
      newProgress = Math.max(0, Math.min(1, newProgress));
      setScrollProgress(newProgress);

      if (bankScrollRef.current) {
        const { scrollWidth, clientWidth } = bankScrollRef.current;
        bankScrollRef.current.scrollLeft =
          newProgress * (scrollWidth - clientWidth);
      }
      if (paymentScrollRef.current) {
        const { scrollWidth, clientWidth } = paymentScrollRef.current;
        paymentScrollRef.current.scrollLeft =
          newProgress * (scrollWidth - clientWidth);
      }
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const paymentScrollRef = useRef(null);

  const handlePaymentScroll = () => {
    if (paymentScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = paymentScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      setScrollProgress(progress);

      if (bankScrollRef.current) {
        const bankMaxScroll =
          bankScrollRef.current.scrollWidth - bankScrollRef.current.clientWidth;
        const targetScroll = progress * bankMaxScroll;
        if (Math.abs(bankScrollRef.current.scrollLeft - targetScroll) > 2) {
          bankScrollRef.current.scrollLeft = targetScroll;
        }
      }
    }
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const [prevProps, setPrevProps] = useState({
    search,
    status,
    fromDate,
    toDate,
    timePeriod,
    vendorId,
  });

  if (
    prevProps.search !== search ||
    prevProps.status !== status ||
    prevProps.fromDate !== fromDate ||
    prevProps.toDate !== toDate ||
    prevProps.timePeriod !== timePeriod ||
    prevProps.vendorId !== vendorId
  ) {
    setCurrentPage(0);
    setPrevProps({ search, status, fromDate, toDate, timePeriod, vendorId });
  }

  const fetchReconciliation = async () => {
    setLoading(true);
    try {
      const params = {
        limit: itemsPerPage,
        skip: currentPage * itemsPerPage,
        search,
        status,
        from_date: fromDate || null,
        to_date: toDate || null,
        vendor_id: vendorId || null,
      };

      if (timePeriod === "LastMonth") {
        const today = new Date();
        const firstDayOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        const lastDayOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0,
        );

        const formatDate = (date) => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, "0");
          const d = String(date.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        };
        params.from_date = formatDate(firstDayOfLastMonth);
        params.to_date = formatDate(lastDayOfLastMonth);
      }

      const response = await getPaymentReconciliationListAPI(params);
      if (response.success) {
        const list = response.reconciliations || [];
        setReconciliationList(list);
        setPageCount(response.total_pgs || 0);

        // Calculate counts
        const total = response.total_rec || list.length;
        const match =
          response.total_match !== undefined
            ? response.total_match
            : response.total_matched !== undefined
              ? response.total_matched
              : list.filter(
                  (item) =>
                    item.status === "MATCHED" || item.status === "VALIDATED",
                ).length;
        const mismatch =
          response.total_mismatch !== undefined
            ? response.total_mismatch
            : response.total_mismatched !== undefined
              ? response.total_mismatched
              : list.filter((item) => item.status === "MISMATCH").length;

        if (onCountsChange) {
          onCountsChange({ total, match, mismatch });
        }
      } else {
        setReconciliationList([]);
        setPageCount(0);
      }
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
      setReconciliationList([]);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReconciliation();
  }, [
    search,
    status,
    fromDate,
    toDate,
    timePeriod,
    vendorId,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    setCurrentPage(0);
  }, [itemsPerPage]);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const displayedList = [...reconciliationList].sort((a, b) => {
    const dateA =
      a.bank_transaction?.transaction_date || a.payment?.transaction_date || "";
    const dateB =
      b.bank_transaction?.transaction_date || b.payment?.transaction_date || "";
    return dateA.localeCompare(dateB);
  });

  const isPageAllSelected = () => {
    const pageItems = displayedList.filter(
      (item) =>
        item.payment &&
        item.status !== "VALIDATED" &&
        item.status !== "MISMATCH",
    );
    return (
      pageItems.length > 0 &&
      pageItems.every((item) => selectedPaymentIds.includes(item.payment.id))
    );
  };

  const handleMismatchClick = (pair) => {
    setActivePair(pair);
    setIsModalOpen(true);
  };

  const handleLinkTransactionOpen = () => {
    setIsModalOpen(false);
    setIsLinkModalOpen(true);
  };

  const handleLinkSubmit = async (bankTransactionId) => {
    if (!activePair || !activePair.payment) return;
    setLinkSubmitting(true);
    try {
      const response = await linkTransactionAPI({
        payment_id: activePair.payment.id,
        bank_transaction_id: bankTransactionId,
      });
      if (response && response.success) {
        setIsLinkModalOpen(false);
        fetchReconciliation();
        showToast(true, response.message || "Transaction linked successfully");
      } else {
        showToast(false, response?.message || "Failed to link transaction");
      }
    } catch (error) {
      console.error("Error linking transaction:", error);
      showToast(
        false,
        error.response?.data?.message || error.message || "An error occurred",
      );
    } finally {
      setLinkSubmitting(false);
    }
  };

  const handleCheckboxChange = (paymentId) => {
    setSelectedPaymentIds((prev) => {
      if (prev.includes(paymentId)) {
        return prev.filter((id) => id !== paymentId);
      } else {
        return [...prev, paymentId];
      }
    });
  };

  const handleSelectAll = (checked) => {
    const pageItems = displayedList.filter(
      (item) =>
        item.payment &&
        item.status !== "VALIDATED" &&
        item.status !== "MISMATCH",
    );
    if (checked) {
      setSelectedPaymentIds((prev) => {
        const newIds = [...prev];
        pageItems.forEach((item) => {
          if (!newIds.includes(item.payment.id)) {
            newIds.push(item.payment.id);
          }
        });
        return newIds;
      });
    } else {
      const pageIds = pageItems.map((item) => item.payment.id);
      setSelectedPaymentIds((prev) =>
        prev.filter((id) => !pageIds.includes(id)),
      );
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const response = await validateReconciliationsAPI({
        payment_ids: selectedPaymentIds,
      });
      if (response && response.success) {
        setSelectedPaymentIds([]);
        fetchReconciliation();
        showToast(
          true,
          response.message || "Transactions validated successfully",
        );
      } else {
        showToast(
          false,
          response?.message || "Failed to validate transactions",
        );
      }
    } catch (error) {
      console.error("Error validating transactions:", error);
      showToast(
        false,
        error.response?.data?.message || error.message || "An error occurred",
      );
    } finally {
      setValidating(false);
    }
  };

  const formatAmount = (amt) => {
    if (amt === undefined || amt === null) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amt);
  };

  return (
    <div
      className="bg-white rounded-4 mt-2 p-3"
      style={{ maxWidth: "100%", overflowX: "hidden" }}
    >
      <div className="reconciliation-header-wrapper mb-2 px-2">
        <div className="reconciliation-header-main">
          <div className="payment-history-header">
            <h5 className="header-text m-0">Payment History</h5>
          </div>
          <div className="hand-icon-header">
            <img
              src={HandIc}
              alt="Drag to scroll"
              style={{
                width: "48px",
                height: "18px",
                opacity: 1,
                objectFit: "contain",
              }}
            />
          </div>
          <div className="d-flex align-items-center justify-content-between bank-transaction-header">
            <h5 className="header-text m-0 text-nowrap">
              Bank Transaction History
            </h5>
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex flex-column align-items-center"
                style={{ gap: "2px" }}
              >
                {/* <span className="text-muted text-nowrap" style={{ color: '#A0A0A0', fontSize: "10px", lineHeight: "1" }}>Drag to scroll table</span> */}
                <div
                  className="scroll-track"
                  ref={trackRef}
                  style={{ margin: "0" }}
                >
                  <div
                    className="scroll-thumb"
                    style={{
                      left: `${scrollProgress * (100 - 30)}px`,
                      cursor: "grab",
                    }}
                    onMouseDown={handleThumbDrag}
                  >
                    <div className="thumb-grip-line" />
                    <div className="thumb-grip-line" />
                    <div className="thumb-grip-line" />
                  </div>
                </div>
              </div>
              <button
                className="btn-validated-green"
                onClick={handleValidate}
                disabled={selectedPaymentIds.length === 0}
                style={{
                  opacity: selectedPaymentIds.length === 0 ? 0.6 : 1,
                  cursor:
                    selectedPaymentIds.length === 0 ? "not-allowed" : "pointer",
                }}
              >
                Validated
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="reconciliation-cards-wrapper">
        {/* Card 1: Payment History */}
        <div
          className="recon-card payment-history-recon-card scrollable-card"
          ref={paymentScrollRef}
          onScroll={handlePaymentScroll}
          style={{ minWidth: 0 }}
        >
          <div
            className="inner-scroll-container"
            style={{ minWidth: "650px", width: "100%" }}
          >
            <table className="table recon-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: "150px" }}>Date</th>
                  <th style={{ width: "250px" }}>Vendor</th>
                  <th style={{ width: "120px" }}>Cheque No.</th>
                  <th style={{ width: "120px" }} className="text-end">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 7 }).map((_, idx) => (
                    <tr key={idx} style={{ height: "44px" }}>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "80px", height: "16px" }}
                        />
                      </td>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "160px", height: "16px" }}
                        />
                      </td>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "70px", height: "16px" }}
                        />
                      </td>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "80px", height: "16px" }}
                        />
                      </td>
                    </tr>
                  ))
                ) : reconciliationList.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      No records found
                    </td>
                  </tr>
                ) : (
                  displayedList.map((item, i) => (
                    <tr
                      key={i}
                      style={{
                        height: "44px",
                        backgroundColor:
                          hoveredIndex === i ? "#F9FAFB" : "#FFFFFF",
                      }}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <td>
                        {item.payment ? item.payment.transaction_date : "--:--"}
                      </td>
                      <td
                        className="fw-500 text-truncate"
                        style={{ maxWidth: "180px" }}
                        title={item.payment ? item.payment.vendor_name : ""}
                      >
                        {item.payment ? item.payment.vendor_name : "--:--"}
                      </td>
                      <td>
                        {item.payment ? item.payment.chequeNo || "-" : "--:--"}
                      </td>
                      <td className="fw-700 text-end">
                        {item.payment
                          ? formatAmount(item.payment.amount)
                          : "--:--"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Card 2: Status */}
        <div className="recon-card status-recon-card">
          <div className="status-header-cell">
            <div className="d-flex align-items-center gap-2">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="2" cy="2" r="1.5" fill="#4B5563" />
                <circle cx="2" cy="6" r="1.5" fill="#4B5563" />
                <circle cx="2" cy="10" r="1.5" fill="#4B5563" />
                <circle cx="6" cy="2" r="1.5" fill="#4B5563" />
                <circle cx="6" cy="6" r="1.5" fill="#4B5563" />
                <circle cx="6" cy="10" r="1.5" fill="#4B5563" />
              </svg>
              <span className="fs-13 fw-700" style={{ color: "#4B5563" }}>
                Status
              </span>
            </div>
            <input
              type="checkbox"
              className="recon-checkbox"
              checked={displayedList.length > 0 && isPageAllSelected()}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
          </div>
          <div className="status-items-container">
            {loading ? (
              Array.from({ length: 7 }).map((_, idx) => (
                <div
                  key={idx}
                  className="status-item-cell justify-content-between"
                  style={{
                    height: "44px",
                    padding: "0 12px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div
                    className="skeleton-shimmer"
                    style={{
                      width: "70px",
                      height: "22px",
                      borderRadius: "100px",
                    }}
                  />
                  <div
                    className="skeleton-shimmer"
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "4px",
                    }}
                  />
                </div>
              ))
            ) : reconciliationList.length === 0 ? (
              <div className="text-center py-4 fs-12 text-secondary">Empty</div>
            ) : (
              displayedList.map((item, i) => (
                <div
                  key={i}
                  className="status-item-cell"
                  style={{
                    height: "44px",
                    backgroundColor: hoveredIndex === i ? "#F9FAFB" : "#FFFFFF",
                  }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <span
                    className={`recon-status-badge ${(item.status || "UNLINKED").toLowerCase()}`}
                    onClick={
                      item.status === "MISMATCH" || item.status === "MATCHED"
                        ? () => handleMismatchClick(item)
                        : undefined
                    }
                    style={{
                      cursor:
                        item.status === "MISMATCH" || item.status === "MATCHED"
                          ? "pointer"
                          : "default",
                    }}
                  >
                    {item.status || "UNLINKED"}
                  </span>
                  {item.payment && item.status !== "VALIDATED" ? (
                    <input
                      type="checkbox"
                      className="recon-checkbox"
                      checked={selectedPaymentIds.includes(item.payment.id)}
                      disabled={item.status === "MISMATCH"}
                      onChange={() => handleCheckboxChange(item.payment.id)}
                    />
                  ) : (
                    <div style={{ width: "16px", height: "16px" }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Card 3: Bank Transaction History */}
        <div
          className="recon-card bank-transaction-recon-card scrollable-card"
          ref={bankScrollRef}
          onScroll={handleBankScroll}
          style={{ minWidth: 0 }}
        >
          <div
            className="inner-scroll-container"
            style={{ minWidth: "780px", width: "100%" }}
          >
            <table className="table recon-table mb-0">
              <thead>
                <tr>
                  <th className="text-nowrap" style={{ width: "120px" }}>
                    Tnx Date
                  </th>
                  <th className="text-nowrap" style={{ width: "120px" }}>
                    Value Date
                  </th>
                  <th className="text-nowrap" style={{ width: "150px" }}>
                    Bank
                  </th>
                  <th>Description</th>
                  <th
                    className="text-nowrap text-end"
                    style={{ width: "120px" }}
                  >
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 7 }).map((_, idx) => (
                    <tr key={idx} style={{ height: "44px" }}>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "80px", height: "16px" }}
                        />
                      </td>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "80px", height: "16px" }}
                        />
                      </td>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "110px", height: "16px" }}
                        />
                      </td>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "160px", height: "16px" }}
                        />
                      </td>
                      <td>
                        <div
                          className="skeleton-shimmer"
                          style={{ width: "80px", height: "16px" }}
                        />
                      </td>
                    </tr>
                  ))
                ) : reconciliationList.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      No records found
                    </td>
                  </tr>
                ) : (
                  displayedList.map((item, i) => (
                    <tr
                      key={i}
                      style={{
                        height: "44px",
                        backgroundColor:
                          hoveredIndex === i ? "#F9FAFB" : "#FFFFFF",
                      }}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <td className="fs-13 text-secondary text-nowrap">
                        {item.bank_transaction
                          ? item.bank_transaction.transaction_date ||
                            item.bank_transaction.dateTime?.split(" at ")[0]
                          : "--:--"}
                      </td>
                      <td className="fs-13 text-secondary text-nowrap">
                        {item.bank_transaction
                          ? item.bank_transaction.value_date ||
                            item.bank_transaction.dateTime?.split(" at ")[0]
                          : "--:--"}
                      </td>
                      <td
                        className="fs-13 text-secondary text-truncate text-nowrap"
                        style={{ maxWidth: "150px" }}
                      >
                        {item.bank_transaction
                          ? item.bank_transaction.bank_name ||
                            item.bank_transaction.bank ||
                            "IDFC First Bank"
                          : "--:--"}
                      </td>
                      <td
                        className="fs-13 text-secondary"
                        title={
                          item.bank_transaction
                            ? item.bank_transaction.description
                            : ""
                        }
                      >
                        {item.bank_transaction
                          ? item.bank_transaction.description ||
                            "UPI/CR/5674..."
                          : "--:--"}
                      </td>
                      <td className="fw-700 text-nowrap text-end">
                        {item.bank_transaction
                          ? formatAmount(item.bank_transaction.spent_amount)
                          : "--:--"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination Footer */}
      {reconciliationList.length > 0 && (
        <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3 px-2 mt-3 border-top">
          <Dropdown>
            <span className="fs-12 fw-600 textcolor me-2">
              Records per page :
            </span>
            <Dropdown.Toggle
              variant="light"
              className="bg-white border textcolor rounded-3 px-2 py-1 fs-12 fw-600"
              style={{ minWidth: "50px" }}
            >
              {itemsPerPage} <DownArrow style={{ width: "10px" }} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {[10, 20, 30, 50].map((size) => (
                <Dropdown.Item
                  key={size}
                  onClick={() => {
                    setItemsPerPage(size);
                  }}
                >
                  {size}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <ReactPaginate
            previousLabel={
              <div
                className="rounded-circle bg-white d-flex align-items-center justify-content-center border"
                style={{ width: "32px", height: "32px" }}
              >
                <LarIc />
              </div>
            }
            nextLabel={
              <div
                className="rounded-circle bg-white d-flex align-items-center justify-content-center border"
                style={{ width: "32px", height: "32px" }}
              >
                <RarIc />
              </div>
            }
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            forcePage={currentPage}
            containerClassName={
              "pagination d-flex gap-2 align-items-center list-unstyled mb-0"
            }
            pageClassName={"page-item"}
            pageLinkClassName={
              "page-link rounded-3 d-flex align-items-center justify-content-center border fs-12 fw-600 textcolor"
            }
            activeClassName={"active"}
            activeLinkClassName={"bg-primary text-white border-primary"}
            disabledClassName={"disabled"}
          />
        </div>
      )}

      <MismatchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onLinkClick={handleLinkTransactionOpen}
        pair={activePair}
        onValidate={async (paymentId) => {
          setValidating(true);
          try {
            const response = await validateReconciliationsAPI({
              payment_ids: [paymentId],
            });
            if (response && response.success) {
              setIsModalOpen(false);
              fetchReconciliation();
              showToast(
                true,
                response.message || "Transaction validated successfully",
              );
            } else {
              showToast(
                false,
                response?.message || "Failed to validate transaction",
              );
            }
          } catch (error) {
            console.error("Error validating transaction:", error);
            showToast(
              false,
              error.response?.data?.message ||
                error.message ||
                "An error occurred",
            );
          } finally {
            setValidating(false);
          }
        }}
        validating={validating}
      />
      <LinkTransactionModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onSubmit={handleLinkSubmit}
        submitting={linkSubmitting}
      />
      <ToastContainer
        position="top-end"
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 100000,
        }}
      >
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default PaymentReconciliation;
